import { Input } from '../../../packages/shared/form-feedback/Native';
import { useState, useEffect,useMemo,useCallback,useRef,useSyncExternalStore } from 'react';
import { API_URL,useAuth } from '../context/AuthContext';
import { warehouseWrites } from '../updates/activity';
import { pickCommandCache } from '../../../packages/shared/preparation/pick-cache';
const pickCache=pickCommandCache();
window.addEventListener('storage',()=>pickCache.refresh());
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useSound } from '../hooks/useSound';
import { TrackedPickForm } from './TrackedPickForm';
import type { IdentityPickReview } from '../../../packages/shared/identity-custody/picking';

interface PickItem {
  id: string;
  sku: string;
  title: string;
  barcode: string;
  quantity: number;
  pickedQuantity: number;
  locationCode: string;
  orderNumber: string;
  imageUrl: string | null;
}

export default function PickItem() {
  const { pickListId, itemId } = useParams();
  const navigate = useNavigate();
  const { fetchApi, loading, error: apiError } = useApi();
  const { playSuccess, playError } = useSound();
  const [item, setItem] = useState<PickItem | null>(null);
  const [quantity, setQuantity] = useState(0);
  const [partnerHeld,setPartnerHeld]=useState(false);
  const [barcode,setBarcode]=useState('');
  const [identities,setIdentities]=useState<IdentityPickReview|null>(null);
  const {user,currentShop}=useAuth();
  const account=useMemo(()=>({userId:String(user?.id||''),organizationId:currentShop?.organizationId||'',domain:currentShop?.domain||''}),[user?.id,currentShop?.organizationId,currentShop?.domain]);
  const context=useRef({account,pickListId,itemId});context.current={account,pickListId,itemId};
  const mounted=useRef(true);useEffect(()=>{mounted.current=true;return()=>{mounted.current=false;};},[]);
  const current=useCallback(()=>mounted.current&&context.current.account===account&&context.current.pickListId===pickListId&&context.current.itemId===itemId,[account,pickListId,itemId]);
  const owner=useMemo(()=>pickCache.get(API_URL,account,Number(pickListId),Number(itemId),current,warehouseWrites.begin),[account,pickListId,itemId,current]),commands=owner.client,command=useSyncExternalStore(commands.subscribe,commands.snapshot);
  useEffect(()=>{owner.activate();return()=>owner.release();},[owner]);
  const saving=command.busy,locked=saving||command.blocked||!!command.pending;
  useEffect(()=>{const changed=()=>commands.refreshSaved();window.addEventListener('storage',changed);return()=>window.removeEventListener('storage',changed);},[commands]);
  useEffect(()=>{if(command.success){playSuccess();navigate(`/pick/${pickListId}`);}},[command.success]);
  useEffect(()=>{if(command.error)playError();},[command.error]);

  useEffect(() => {
    loadItem();
  }, [itemId,pickListId,account]);

  async function loadItem() {
    setItem(null);setIdentities(null);
    const [result,review] = await Promise.all([fetchApi<{ pickList: { items: PickItem[];partner_work?:{stage:string}|null } }>(`/picking/lists/${pickListId}`),fetchApi<IdentityPickReview>(`/picking/lists/${pickListId}/items/${itemId}/identities`)]);
    if(!current())return;
    if(review?.actor_id==='user:'+account.userId&&review.pick_list_id===Number(pickListId)&&review.order_item_id===Number(itemId)&&typeof review.tracked==='boolean'&&Array.isArray(review.entries)&&Array.isArray(review.blockers)&&/^[a-f0-9]{64}$/.test(review.digest))setIdentities(review);
    setPartnerHeld(!!result?.pickList.partner_work);
    const data = { item: result?.pickList.items.find(item => String(item.id) === itemId) };
    if (data?.item) {
      setItem(data.item);
      setQuantity(data.item.quantity - data.item.pickedQuantity);
    }
  }

  const handleConfirmPick = async () => {if(item&&identities&&!identities.tracked&&!identities.blockers.length&&!locked)await commands.submit(quantity,item.pickedQuantity,barcode);};
  const recovery=<>{command.error&&<p role="alert">{command.error}</p>}{command.pending&&<div role="alert"><p>This pick has no confirmed reply. Retry the saved pick before recording more goods.</p><button className="btn btn-secondary" disabled={saving} onClick={()=>void commands.retry()}>Retry saved pick</button></div>}</>;

  const handleQuantityChange = (delta: number) => {
    const newQty = Math.max(0, Math.min((item?.quantity||0)-(item?.pickedQuantity||0), quantity + delta));
    setQuantity(newQty);
  };

  if (loading || !item) {
    return (
      <div className="page">
        <div className="page-content">{recovery}{apiError&&<p role="alert">{apiError}</p>}{loading?<p>Loading item…</p>:<p>This item is no longer on this pick list.</p>}<button onClick={()=>navigate(`/pick/${pickListId}`)}>Back to pick list</button></div>
      </div>
    );
  }

  if (partnerHeld) return <div className="page"><div className="page-content">{recovery}
    <p className="card">A warehouse partner holds this pick list. Ask for the list back before picking more units.</p>
    <button onClick={()=>navigate(`/pick/${pickListId}`)}>Back to pick list</button></div></div>;
  const remaining = item.quantity - item.pickedQuantity;

  return (
    <div className="page">
      <div className="page-header">
        <button
          onClick={() => navigate(`/pick/${pickListId}`)}
          style={{ background: 'none', border: 'none', fontSize: '1.5rem' }}
        >
          ←
        </button>
        <h1>Pick Item</h1>
      </div>

      <div className="page-content">
        {apiError && <p role="alert" style={{ color: 'var(--danger)' }}>{apiError}</p>}{recovery}
        {/* Product Image */}
        {item.imageUrl && (
          <div
            style={{
              background: 'white',
              borderRadius: 'var(--radius)',
              padding: '1rem',
              marginBottom: '1rem',
              textAlign: 'center',
            }}
          >
            <img
              src={item.imageUrl}
              alt={item.title}
              style={{ maxWidth: '100%', maxHeight: '150px', objectFit: 'contain' }}
            />
          </div>
        )}

        {/* Product Info */}
        <div className="card">
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            {item.sku}
          </div>
          <div style={{ marginBottom: '1rem' }}>{item.title}</div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>LOCATION</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--primary)' }}>
                📍 {item.locationCode}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>ORDER</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
                #{item.orderNumber}
              </div>
            </div>
          </div>

          {item.barcode && (
            <div style={{ marginTop: '1rem', padding: '0.5rem', background: 'var(--gray-100)', borderRadius: '8px' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>BARCODE</div>
              <div style={{ fontFamily: 'monospace' }}>{item.barcode}</div>
            </div>
          )}
        </div>

        {!identities&&<div role="alert"><p>The current identity review could not be confirmed.</p><button disabled={locked} onClick={()=>void loadItem()}>Refresh picking review</button></div>}
        {identities?.tracked&&<TrackedPickForm key={identities.digest} review={identities} base={API_URL} account={account} picked={item.pickedQuantity} commands={commands} locked={locked}/>}
        {identities&&!identities.tracked&&<>
        <div className="card"><label>Scan or enter product barcode<Input value={barcode} onChange={e=>setBarcode(e.target.value)} maxLength={255} disabled={locked} autoComplete="off" /></label><p>Required when your company requires a product scan.</p></div>
        {/* Quantity to Pick */}
        <div className="card">
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>PICK QUANTITY *</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
              {item.pickedQuantity} already picked, {remaining} remaining
            </div>
          </div>

          <div className="quantity-stepper" style={{ justifyContent: 'center' }}>
            <button onClick={() => handleQuantityChange(-1)}>−</button>
            <Input
              type="number"
              required aria-label="Pick quantity" value={quantity}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10) || 0;
                setQuantity(Math.max(0, Math.min(remaining, val)));
              }}
            />
            <button onClick={() => handleQuantityChange(1)}>+</button>
          </div>

          <div style={{ textAlign: 'center', marginTop: '0.5rem', color: 'var(--gray-500)' }}>
            of {remaining} remaining
          </div>
        </div>

        {/* Quick Pick Buttons */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          <button
            className="btn btn-secondary"
            style={{ flex: 1 }}
            onClick={() => setQuantity(remaining)}
          >
            Pick All ({remaining})
          </button>
          <button
            className="btn btn-secondary"
            style={{ flex: 1 }}
            onClick={() => setQuantity(0)}
          >
            Clear
          </button>
        </div>
        </>}
      </div>

      {/* Bottom Actions */}
      <div className="bottom-actions">
        <button
          className="btn btn-secondary"
          style={{ flex: 1 }}
          onClick={() => navigate('/scanner')}
        >
          📷 Scan
        </button>
        {!identities?.tracked&&<button
          className="btn btn-success"
          style={{ flex: 2 }}
          onClick={handleConfirmPick}
          disabled={locked || !identities || !!identities.blockers.length || quantity === 0}
        >
          {saving ? 'Saving...' : `✓ Confirm Pick (${quantity})`}
        </button>}
      </div>
    </div>
  );
}
